import { useState, useMemo, useEffect } from 'react';
import type { FloatingPosition } from '@mantine/core';
import {
  Combobox,
  useCombobox,
  InputBase,
  PillsInput,
  Pill,
  ScrollArea,
} from '@mantine/core';
import { ChevronDown } from 'react-feather';
import { sortOptionsBySearchText } from './selectionUtils';
import { useMultiSelect, handleBackspaceRemoval } from './useMultiSelect';
import { renderOption } from './Option';
import { renderMultiOption } from './MultiOption';
import styles from './InputSelect.module.css';

export type Option = {
  value: string | null;
  label: string;
  subLabel?: string;
};

type RenderableOption = Option & { value: string };

interface Props {
  /**
   * Placeholder when there is no selection
   */
  placeholder?: string;
  /**
   * Optional label for input select
   */
  label?: string;
  /**
   * Is multi select
   */
  isMulti?: boolean;
  /**
   * Filter contents
   */
  options: Option[];
  /**
   * Optional change handler
   */
  onChange: (e: unknown) => void;
  /**
   * Optional blur handler
   */
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  /**
   * Selected value/s
   */
  value?: Option | Option[];
  /**
   * Dropdown menu placement. Omit to let Mantine auto-place based on viewport space.
   */
  menuPlacement?: FloatingPosition;
  /**
   * Is input select disabled
   */
  isDisabled?: boolean;
  maxMenuHeight?: number;
  /**
   * Allow shift-click to select ranges
   */
  allowShiftSelect?: boolean;
}

export const InputSelect = ({
  options,
  label,
  onChange,
  onBlur,
  value,
  placeholder,
  isMulti = false,
  menuPlacement,
  maxMenuHeight = 185,
  isDisabled = false,
  allowShiftSelect = false,
}: Props) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [optionSelected, setOptionSelected] = useState(value);
  const [inputValue, setInputValue] = useState('');
  const { captureShiftKey, handleClick } = useMultiSelect<Option>({
    allowShiftSelect,
    getKey: (item) => item.value,
  });

  useEffect(() => {
    setOptionSelected(value);
  }, [value]);

  const sortedOptions = useMemo(
    () => sortOptionsBySearchText(options, inputValue),
    [options, inputValue]
  );

  const visibleOptions = useMemo(() => {
    if (!inputValue) return sortedOptions;
    const lower = inputValue.toLowerCase();
    return sortedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(lower)
    );
  }, [sortedOptions, inputValue]);

  const selectedSingle =
    optionSelected && !Array.isArray(optionSelected) ? optionSelected : null;
  const selectedMulti = Array.isArray(optionSelected) ? optionSelected : [];

  const handleChange = (selected: Option | Option[] | null) => {
    setOptionSelected(selected ?? undefined);
    if (Array.isArray(selected)) {
      onChange(selected.map((s) => s.value));
    } else if (selected) {
      onChange(selected.value);
    } else {
      onChange(selected);
    }
  };

  const isOptionSelected = (opt: Option) =>
    selectedMulti.some((s) => s.value === opt.value);

  const handleOptionSubmit = (val: string) => {
    if (isMulti) {
      const selection = handleClick({
        val,
        visibleOptions,
        currentSelection: selectedMulti,
      });
      if (selection) handleChange(selection);
    } else {
      const option = sortedOptions.find((o) => o.value === val);
      handleChange(option ?? null);
      combobox.closeDropdown();
    }
  };

  const removeOption = (val: string | null) => {
    handleChange(selectedMulti.filter((s) => s.value !== val));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    handleBackspaceRemoval(e, inputValue, selectedMulti, (val) =>
      removeOption(val)
    );

  const testLabel = label ?? 'input-select';

  return (
    <div
      className={styles.root}
      data-testid={testLabel}
      data-test-role="dropdown"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          combobox.closeDropdown();
        }
      }}
    >
      {label && <span className={styles.label}>{label}</span>}
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSubmit}
        position={menuPlacement}
        keepMounted={false}
        withinPortal={false}
      >
        {isMulti ? (
          <Combobox.DropdownTarget>
            <PillsInput
              size="md"
              onClick={(e) => {
                if (isDisabled) return;
                // Skip clicks on the pill remove × so removing a pill doesn't
                // also toggle the dropdown.
                if ((e.target as HTMLElement).closest('button')) return;
                if (combobox.dropdownOpened) {
                  combobox.closeDropdown();
                } else {
                  combobox.openDropdown();
                }
              }}
              disabled={isDisabled}
              classNames={{ input: styles.input }}
              rightSection={
                <ChevronDown
                  size={16}
                  color="var(--mantine-color-grayBrand-7)"
                />
              }
              rightSectionPointerEvents="none"
            >
              <Pill.Group gap="var(--mantine-spacing-xxs)">
                {selectedMulti.map((opt) => (
                  <Pill
                    size="md"
                    key={opt.value}
                    withRemoveButton={!isDisabled}
                    onRemove={() => removeOption(opt.value)}
                    classNames={{
                      root: styles.pill,
                      remove: styles.pillRemove,
                    }}
                  >
                    {opt.label}
                  </Pill>
                ))}
                <Combobox.EventsTarget>
                  <PillsInput.Field
                    role="combobox"
                    className={styles.field}
                    value={inputValue}
                    placeholder={selectedMulti.length === 0 ? placeholder : ''}
                    onChange={(e) => {
                      setInputValue(e.currentTarget.value);
                      combobox.openDropdown();
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      combobox.closeDropdown();
                      onBlur(e);
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={isDisabled}
                  />
                </Combobox.EventsTarget>
              </Pill.Group>
            </PillsInput>
          </Combobox.DropdownTarget>
        ) : (
          <Combobox.Target>
            <InputBase
              readOnly
              size="md"
              role="combobox"
              aria-expanded={combobox.dropdownOpened}
              aria-haspopup="listbox"
              pointer
              placeholder={placeholder}
              value={selectedSingle?.label ?? ''}
              data-testid="input-select-single"
              onClick={() => !isDisabled && combobox.toggleDropdown()}
              onBlur={(e) => {
                combobox.closeDropdown();
                onBlur(e);
              }}
              disabled={isDisabled}
              classNames={{ input: styles.input }}
              rightSection={
                <ChevronDown
                  size={16}
                  color="var(--mantine-color-grayBrand-7)"
                />
              }
              rightSectionPointerEvents="none"
            />
          </Combobox.Target>
        )}

        <Combobox.Dropdown className={styles.dropdown}>
          <ScrollArea.Autosize mah={maxMenuHeight}>
            <Combobox.Options
              onMouseDownCapture={isMulti ? captureShiftKey : undefined}
            >
              {(visibleOptions as RenderableOption[]).map((opt) => (
                <Combobox.Option
                  value={opt.value}
                  key={opt.value}
                  data-testid={opt.value}
                  className={styles.dropdownOption}
                  selected={
                    isMulti
                      ? isOptionSelected(opt)
                      : selectedSingle?.value === opt.value
                  }
                >
                  {isMulti
                    ? renderMultiOption({
                        option: opt,
                        checked: isOptionSelected(opt),
                      })
                    : renderOption({
                        option: opt,
                        checked: selectedSingle?.value === opt.value,
                      })}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          </ScrollArea.Autosize>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
};
